import React, { useState, useEffect } from 'react';
import CustomerLayout from '../components/CustomerLayout';
import { customerAPI } from '../lib/api';
import '../App.css';

export default function Domains() {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');

    const [selectedDomain, setSelectedDomain] = useState(null);
    const [domainDetail, setDomainDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [authCode, setAuthCode] = useState(null);

    useEffect(() => {
        fetchDomains();
    }, [filter]);

    const fetchDomains = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await customerAPI.getDomains(params);
            setDomains(response.data.domains || []);
        } catch (err) {
            console.error('Error fetching domains:', err);
            setError(err.response?.data?.error || 'Failed to load domains');
        } finally {
            setLoading(false);
        }
    };

    const isDnsOnly = (domain) => domain.metadata?.domain_type === 'dns_only' || domain.registrar === 'cloudflare';

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU');
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return '#28a745';
            case 'expired': return '#dc3545';
            case 'pending': return '#17a2b8';
            case 'cancelled': return '#6c757d';
            case 'transferred_out': return '#ffc107';
            default: return '#6c757d';
        }
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'Active';
            case 'expired': return 'Expired';
            case 'pending': return 'Pending';
            case 'cancelled': return 'Cancelled';
            case 'transferred_out': return 'Transferred Away';
            default: return status || 'Unknown';
        }
    };

    const getExpiryBadge = (domain) => {
        if (!domain.expiration_date || isDnsOnly(domain)) return null;
        const daysUntilExpiry = Math.ceil((new Date(domain.expiration_date) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry < 0) {
            return <span className="status-badge" style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#dc3545', color: 'white', padding: '2px 8px', borderRadius: '4px' }}>Expired</span>;
        }
        if (daysUntilExpiry <= 30) {
            return <span className="status-badge" style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#ffc107', color: 'white', padding: '2px 8px', borderRadius: '4px' }}>{daysUntilExpiry} days</span>;
        }
        return null;
    };

    const openManageModal = async (domain) => {
        setSelectedDomain(domain);
        setDomainDetail(null);
        setDetailError(null);
        setActionError(null);
        setAuthCode(null);
        setDetailLoading(true);
        try {
            const response = await customerAPI.getDomain(domain.domain_id);
            setDomainDetail(response.data);
        } catch (err) {
            console.error('Error fetching domain details:', err);
            setDetailError(err.response?.data?.error || 'Failed to load domain details');
        } finally {
            setDetailLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedDomain(null);
        setDomainDetail(null);
        setDetailError(null);
        setActionError(null);
        setAuthCode(null);
    };

    const handleToggleAutoRenew = async () => {
        const domain = domainDetail.domain;
        const nextEnabled = !domain.auto_renew;
        if (!window.confirm(`${nextEnabled ? 'Enable' : 'Disable'} auto-renew for ${domain.domain_name}?`)) return;

        setActionLoading(true);
        setActionError(null);
        try {
            await customerAPI.toggleDomainAutoRenew(domain.domain_id, nextEnabled);
            setDomainDetail({ ...domainDetail, domain: { ...domain, auto_renew: nextEnabled } });
            setDomains(domains.map((d) => (d.domain_id === domain.domain_id ? { ...d, auto_renew: nextEnabled } : d)));
        } catch (err) {
            console.error('Error toggling auto-renew:', err);
            setActionError(err.response?.data?.error || 'Failed to update auto-renew');
        } finally {
            setActionLoading(false);
        }
    };

    const handleGetAuthCode = async () => {
        const domain = domainDetail.domain;
        setActionLoading(true);
        setActionError(null);
        try {
            const response = await customerAPI.getDomainAuthCode(domain.domain_id);
            setAuthCode(response.data.auth_code);
        } catch (err) {
            console.error('Error retrieving auth code:', err);
            setActionError(err.response?.data?.error || 'Failed to retrieve auth code');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRenew = async () => {
        const domain = domainDetail.domain;
        if (!window.confirm(`Renew ${domain.domain_name} for 1 year?`)) return;

        setActionLoading(true);
        setActionError(null);
        try {
            const response = await customerAPI.renewDomain(domain.domain_id, 1);
            setDomainDetail({
                ...domainDetail,
                domain: { ...domain, expiration_date: response.data.new_expiration },
                renewals: [response.data.renewal, ...domainDetail.renewals],
            });
            setDomains(domains.map((d) => (d.domain_id === domain.domain_id ? { ...d, expiration_date: response.data.new_expiration } : d)));
        } catch (err) {
            console.error('Error renewing domain:', err);
            setActionError(err.response?.data?.error || 'Failed to renew domain');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <CustomerLayout>
            <div style={{ maxWidth: '1200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div>
                        <h1>Domains</h1>
                        <p style={{ color: '#666', marginTop: '8px' }}>View and manage your domains</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <select
                            className="form-input"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            style={{ width: 'auto' }}
                        >
                            <option value="all">All Domains</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="expired">Expired</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error">{error}</div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div className="spinner"></div>
                        <p style={{ marginTop: '20px', color: '#666' }}>Loading domains...</p>
                    </div>
                ) : domains.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
                        <p style={{ fontSize: '18px', color: '#666' }}>No domains found</p>
                    </div>
                ) : (
                    <div className="card">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Domain</th>
                                    <th>Registrar</th>
                                    <th>Expiry Date</th>
                                    <th>Status</th>
                                    <th>Auto-Renew</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {domains.map((domain) => (
                                    <tr key={domain.domain_id}>
                                        <td><strong>{domain.domain_name}</strong></td>
                                        <td style={{ textTransform: 'capitalize' }}>{domain.registrar}</td>
                                        <td>
                                            {domain.expiration_date ? formatDate(domain.expiration_date) : 'N/A'}
                                            {getExpiryBadge(domain)}
                                        </td>
                                        <td>
                                            <span
                                                className="status-badge"
                                                style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '500',
                                                    backgroundColor: getStatusColor(domain.status),
                                                    color: 'white',
                                                }}
                                            >
                                                {getStatusLabel(domain.status)}
                                            </span>
                                        </td>
                                        <td>
                                            {isDnsOnly(domain) ? (
                                                <span style={{ color: '#999' }}>-</span>
                                            ) : domain.auto_renew ? (
                                                <span style={{ color: '#28a745', fontWeight: '500' }}>Yes</span>
                                            ) : (
                                                <span style={{ color: '#999' }}>No</span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => openManageModal(domain)}
                                                style={{
                                                    padding: '6px 16px',
                                                    backgroundColor: '#1976d2',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                Manage
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Manage Domain Modal */}
            {selectedDomain && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                    }}
                    onClick={closeModal}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            maxWidth: '700px',
                            width: '95%',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px 30px',
                            borderBottom: '1px solid #e0e0e0',
                        }}>
                            <h2 style={{ margin: 0 }}>{selectedDomain.domain_name}</h2>
                            <button className="btn btn-sm" onClick={closeModal} style={{ fontSize: '12px', padding: '6px 12px' }}>
                                ✕ Close
                            </button>
                        </div>

                        <div style={{ padding: '30px', overflowY: 'auto', flex: 1 }}>
                            {detailLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div className="spinner"></div>
                                </div>
                            ) : detailError ? (
                                <div className="alert alert-error">{detailError}</div>
                            ) : domainDetail ? (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                                        <div>
                                            <h4 style={{ marginBottom: '10px', color: '#666' }}>Registrar</h4>
                                            <p style={{ textTransform: 'capitalize' }}>{domainDetail.domain.registrar}</p>
                                        </div>
                                        <div>
                                            <h4 style={{ marginBottom: '10px', color: '#666' }}>Status</h4>
                                            <span
                                                className="status-badge"
                                                style={{ backgroundColor: getStatusColor(domainDetail.domain.status), color: 'white' }}
                                            >
                                                {getStatusLabel(domainDetail.domain.status)}
                                            </span>
                                        </div>
                                        <div>
                                            <h4 style={{ marginBottom: '10px', color: '#666' }}>Registration Date</h4>
                                            <p>{formatDate(domainDetail.domain.registration_date)}</p>
                                        </div>
                                        <div>
                                            <h4 style={{ marginBottom: '10px', color: '#666' }}>Expiry Date</h4>
                                            <p>{formatDate(domainDetail.domain.expiration_date)}</p>
                                        </div>
                                    </div>

                                    {domainDetail.domain.nameservers && domainDetail.domain.nameservers.length > 0 && (
                                        <div style={{ marginBottom: '30px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#666' }}>Nameservers</h4>
                                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                {domainDetail.domain.nameservers.map((ns, i) => <li key={i}>{ns}</li>)}
                                            </ul>
                                        </div>
                                    )}

                                    {actionError && (
                                        <div className="alert alert-error" style={{ marginBottom: '20px' }}>{actionError}</div>
                                    )}

                                    {!isDnsOnly(domainDetail.domain) && (
                                        <div style={{
                                            padding: '20px',
                                            background: '#f8f9fa',
                                            borderRadius: '8px',
                                            border: '1px solid #dee2e6',
                                            marginBottom: '30px',
                                        }}>
                                            <h4 style={{ marginBottom: '15px', fontSize: '16px' }}>Self-Service Actions</h4>
                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: authCode ? '15px' : 0 }}>
                                                <button
                                                    className="btn btn-secondary"
                                                    disabled={actionLoading}
                                                    onClick={handleToggleAutoRenew}
                                                >
                                                    {domainDetail.domain.auto_renew ? 'Disable' : 'Enable'} Auto-Renew
                                                </button>
                                                <button
                                                    className="btn btn-secondary"
                                                    disabled={actionLoading}
                                                    onClick={handleGetAuthCode}
                                                >
                                                    Get Transfer Code
                                                </button>
                                                <button
                                                    className="btn btn-primary"
                                                    disabled={actionLoading}
                                                    onClick={handleRenew}
                                                >
                                                    Renew (1 year)
                                                </button>
                                            </div>

                                            {authCode && (
                                                <div style={{
                                                    padding: '15px',
                                                    background: 'white',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dee2e6',
                                                }}>
                                                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>Transfer/Auth Code:</p>
                                                    <p style={{ margin: '0 0 10px 0', fontFamily: 'monospace', fontSize: '16px', fontWeight: '600' }}>{authCode}</p>
                                                    <div style={{
                                                        padding: '10px',
                                                        background: '#fff3cd',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ffc107',
                                                    }}>
                                                        <p style={{ margin: 0, color: '#856404', fontSize: '13px', fontStyle: 'italic' }}>
                                                            ⚠️ Anyone with this code can initiate a domain transfer. Keep it confidential.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {domainDetail.renewals && domainDetail.renewals.length > 0 && (
                                        <div>
                                            <h4 style={{ marginBottom: '15px' }}>Renewal History</h4>
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th>Renewed Until</th>
                                                        <th>Amount</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {domainDetail.renewals.map((renewal) => (
                                                        <tr key={renewal.renewal_id}>
                                                            <td>{formatDate(renewal.renewed_until)}</td>
                                                            <td>{renewal.amount ? `${renewal.currency || ''} ${renewal.amount}` : 'N/A'}</td>
                                                            <td style={{ textTransform: 'capitalize' }}>{renewal.status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </CustomerLayout>
    );
}
